import { type QConfigTableData } from '@exam-management/common/types';
import { QuestionTypeChip } from '@exam-management/components';
import { QConfigStatus, QConfigStructureCategory } from '@exam-management/graphql';
import {
  Box,
  Chip,
  EllipsisText,
  Flex,
  styled,
  TableCell,
  TableRow,
  Text,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';
import React from 'react';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { getQuestionChipType } from '../utils';

import { TopicTableRowMenu, type TopicTableRowMenuProps } from './topic-table-row-menu';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: '1px solid',
  borderColor: theme.palette.grey[300],
}));

export interface TopicTableRowProps extends Pick<TopicTableRowMenuProps, 'onUpdate'> {
  rows: QConfigTableData[];
  data: QConfigTableData;
  status?: QConfigStatus;
}

export default function TopicTableRow(props: Readonly<TopicTableRowProps>) {
  const { rows, data, status, onUpdate } = props;

  const { totalQuestions, scorePerQuestion, calculated, notCalculated, questionType, size } = data;
  const questionChipType = getQuestionChipType(questionType);
  const isQConfigDraft = status === QConfigStatus.Draft;

  return (
    <TableRow
      sx={{
        border: 'none',
        '& :last-of-type': {
          borderRight: 'none',
          td: {
            borderBottom: 'none',
          },
        },
      }}
    >
      <StyledTableCell
        colSpan={2}
        sx={{
          p: (theme) => theme.spacing(0),
          borderBottom: 'none',
        }}
      >
        <Flex
          sx={{ minHeight: '48px', backgroundColor: (theme) => theme.palette.background.paper }}
        >
          <Box
            sx={{
              minWidth: '48px',
              borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`,
            }}
          />
          <Flex
            sx={(theme) => ({
              width: '100%',
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              p: theme.spacing(2, 3, 2, 14),
              alignItems: 'center',
            })}
          >
            <Flex sx={(theme) => ({ gap: theme.spacing(2) })}>
              <Chip
                label='Topic'
                size='small'
                sx={{
                  bgcolor: (theme) => theme.palette.info.light,
                  fontWeight: 600,
                  width: '48px',
                }}
              />
              <Flex sx={{ gap: (theme) => theme.spacing(2), flexDirection: 'column' }}>
                {rows
                  .filter((row) => row.category === QConfigStructureCategory.Topic)
                  .map((row) => {
                    const subTopicRows = rows.filter((subRow) => subRow.parentIds.includes(row.id));
                    return (
                      <React.Fragment key={row.id}>
                        <EllipsisText variant='body2Semibold' sx={{ justifyContent: 'center' }}>
                          {row.name}
                        </EllipsisText>
                        <Flex sx={{ gap: (theme) => theme.spacing(2) }}>
                          <Text variant='captionSemibold' sx={{ flexShrink: 0 }}>
                            Sub-Topic:
                          </Text>
                          <Flex
                            sx={{
                              gap: (theme) => theme.spacing(1),
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                            }}
                          >
                            {subTopicRows.map((subTopicRow) => (
                              <EllipsisText key={subTopicRow.id} variant='caption'>
                                {subTopicRow.name}
                              </EllipsisText>
                            ))}
                          </Flex>
                        </Flex>
                      </React.Fragment>
                    );
                  })}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
          alignContent: 'baseline',
        }}
      >
        {totalQuestions}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
          alignContent: 'baseline',
        }}
      >
        {scorePerQuestion}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={(theme) => ({ alignContent: 'baseline', p: theme.spacing(3, 2) })}
      >
        {questionChipType ? <QuestionTypeChip type={questionChipType} questionSize={size} /> : '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.easy ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.medium ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {calculated.hard ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {notCalculated.easy ?? '-'}
      </StyledTableCell>
      <StyledTableCell align='center' sx={{ alignContent: 'baseline' }}>
        {notCalculated.medium ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          alignContent: 'baseline',
          borderRight: !isQConfigDraft ? 'none' : undefined,
        }}
      >
        {notCalculated.hard ?? '-'}
      </StyledTableCell>
      <WithPermission requiredPermission={EDIT_QCONFIG_REQUIRED_PERMISSIONS}>
        {isQConfigDraft ? (
          <StyledTableCell
            align='center'
            sx={{
              borderRight: 'none',
              alignContent: 'baseline',
            }}
          >
            <TopicTableRowMenu qConfigStructureId={data.id} onUpdate={onUpdate} />
          </StyledTableCell>
        ) : null}
      </WithPermission>
    </TableRow>
  );
}
